import React, {ReactElement} from 'react';
import ReactDOM from 'react-dom';
import {useSelector} from 'react-redux';

import LandingTemplate from 'App/layout/components/landingTemplate';
import ModalTemplate from 'App/layout/components/modalTemplate';
import ProjectEditionTemplate from 'App/layout/components/projectEditionTemplate';
import Reparentable from 'App/layout/components/reparentable';
import WindowTemplate from 'App/layout/components/windowTemplate';
import {LAYOUT_MODES} from 'App/layout/layoutConstants';
import {selectLayoutMode} from 'App/layout/layoutSlice';

type TProps = {
  content: ReactElement;
  map: ReactElement;
};

const contentContainer: HTMLElement = document.createElement('div');
contentContainer.className = 'w-full min-h-full';
const mapContainer: HTMLElement = document.createElement('div');
mapContainer.className = 'w-full';

const ResponsiveContentView = ({content, map}: TProps): ReactElement => {
  const layoutMode = useSelector(selectLayoutMode);

  // The map is quite heavy, so we don't want to unmount and mount it again.
  // To avoid it we:
  // 1. Introduce mapContainer, which is just DOM element, that's
  // not integrated anywhere;
  // 2. Send map to mapContainer via portal;
  // 3. Add mapContainer to DOM directly via <Reparentable />
  // using useEffect and append()
  // https://github.com/facebook/react/issues/13044#issuecomment-428815909
  const reparentableContent = <Reparentable element={contentContainer} />;
  const reparentableMap = <Reparentable element={mapContainer} />;

  return (
    <>
      {ReactDOM.createPortal(content, contentContainer)}
      {ReactDOM.createPortal(map, mapContainer)}

      {layoutMode === LAYOUT_MODES.WINDOW && (
        <WindowTemplate
          content={reparentableContent}
          reparentableMap={reparentableMap}
        />
      )}
      {layoutMode === LAYOUT_MODES.PROJECT_EDITION && (
        <ProjectEditionTemplate
          reparentableContent={reparentableContent}
          reparentableMap={reparentableMap}
        />
      )}
      {layoutMode === LAYOUT_MODES.MODAL && (
        <ModalTemplate
          content={reparentableContent}
          reparentableMap={reparentableMap}
        />
      )}
      {layoutMode === LAYOUT_MODES.LANDING && (
        <LandingTemplate
          content={reparentableContent}
          reparentableMap={reparentableMap}
        />
      )}
    </>
  );
};

export default ResponsiveContentView;
